import React, { useEffect, useRef, useState } from "react";
import {
    Modal,
    Form,
    Select,
    Button,
    Input,
    InputNumber,
    Tag,
    Empty,
    Space,
    Typography,
    Divider,
    Alert,
    message,
    Tooltip,
    Card,
    Table,
    Grid
} from "antd";
import { t } from "i18next";
import { v4 } from "uuid";
import { PlusOutlined, DeleteOutlined, SaveOutlined, ClearOutlined, SearchOutlined } from "@ant-design/icons";
import ApiService from "../../core/service/ApiService";
import Helper from "../../core/Helper";
import { useUser } from "../../core/context/UserContext";
import AppointmentTypeSelect from "../appointment/AppointmentTypeSelect";
import { Currency } from "../Payment/MultiplePaymentApp";
import { PriceQuoteStorage } from "../../core/service/PriceQuoteStorage";

const { Text } = Typography;

export default function PriceQuoteModal({ open, onClose, onSaved }) {
    const { formatPrice, firmSettings } = useUser();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    // Mobilde Birim / Randevu Türü search'ünü kapat (iOS klavyesi
    // modal açılışında otomatik tetiklenmesin diye)
    const enableSearch = !isMobile;

    // Master data
    const [units, setUnits] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [operationList, setOperationList] = useState([]);
    const appointmentTypesRef = useRef([]);

    // Selections
    const [unitId, setUnitId] = useState(null);
    const [institutionId, setInstitutionId] = useState(null);
    const [appointmentTypeId, setAppointmentTypeId] = useState(null);
    const [currency, setCurrency] = useState('TRY');
    // Wizard adım düzenleme: completed bir adıma tıklanınca açılır,
    // yeniden tıklanınca (veya farklı bir değer seçilince) kapanır
    const [editingStep, setEditingStep] = useState(null);
    // Kurum (step 2) arama
    const [institutionSearch, setInstitutionSearch] = useState('');
    const [operationSelectOpen, setOperationSelectOpen] = useState(false);
    const [operationPickerOpen, setOperationPickerOpen] = useState(false);
    const [operationPickerSearch, setOperationPickerSearch] = useState('');
    const operationPickerSearchRef = useRef(null);
    // iOS mobil klavye primer'ı — user-gesture synchronous focus için
    const operationKeyboardPrimerRef = useRef(null);

    useEffect(() => {
        if (!operationPickerOpen) return;
        const timer = setTimeout(() => {
            operationPickerSearchRef.current?.focus();
        }, 250);
        return () => clearTimeout(timer);
    }, [operationPickerOpen]);

    const openOperationPicker = () => {
        try {
            operationKeyboardPrimerRef.current?.focus({ preventScroll: true });
        } catch { /* ignore */ }
        setOperationPickerSearch('');
        setOperationPickerOpen(true);
    };

    // Selected operations & others
    const [selectedOps, setSelectedOps] = useState([]); // { id, operationId, operation, price, accessionNumber, processMinute }
    const [otherOps, setOtherOps] = useState([]); // { id, name, price }
    const [otherForm, setOtherForm] = useState({ name: '', price: 0 });

    // Price refs (AppointmentDetailEdit ile aynı pattern)
    const priceListRef = useRef([]); // institution prices
    const appTypePriceListRef = useRef([]); // appointment type prices
    const institutionIdRef = useRef(null);
    const appointmentTypeIdRef = useRef(null);

    // --- LOAD MASTER DATA ---
    const loadUnits = async () => {
        const res = await ApiService.Unit.GetUnitList();
        if (res.success) setUnits(res.data);
    };

    const loadInstitutions = async (firmId) => {
        if (!firmId) {
            setInstitutions([]);
            return [];
        }
        const res = await ApiService.Institution.GetInstitutionListByFirm(firmId);
        if (res.success) {
            setInstitutions(res.data);
            return res.data;
        }
        return [];
    };

    const loadOperations = async (uId) => {
        if (!uId) {
            setOperationList([]);
            return [];
        }
        const res = await ApiService.Operation.GetOperationList(uId);
        if (res.success) {
            const ops = res.data.map(x => ({
                ...x,
                subItems: JSON.parse(x.subItems ?? '[]')
            }));
            setOperationList(ops);
            return ops;
        }
        return [];
    };

    const loadInstitutionPriceList = async (insId, opIds) => {
        if (institutionIdRef.current === insId) return;
        if (!insId || !opIds || opIds.length === 0) {
            priceListRef.current = [];
            institutionIdRef.current = insId;
            return;
        }
        const res = await ApiService.Operation.GetInstitutionPriceList(insId, opIds);
        if (res.success) {
            priceListRef.current = res.data;
            institutionIdRef.current = insId;
        }
    };

    const loadAppointmentTypePriceList = async (atId, opIds) => {
        if (appointmentTypeIdRef.current === atId) return;
        if (!atId || !opIds || opIds.length === 0) {
            appTypePriceListRef.current = [];
            appointmentTypeIdRef.current = atId;
            return;
        }
        const res = await ApiService.Operation.GetAppointmentTypePriceList(atId, opIds);
        if (res.success) {
            appTypePriceListRef.current = res.data;
            appointmentTypeIdRef.current = atId;
        }
    };

    // --- INITIAL LOAD ---
    useEffect(() => {
        if (!open) return;
        loadUnits();
    }, [open]);

    // --- BODY SCROLL LOCK (iOS Safari için: antd'nin default'u yetmiyor) ---
    useEffect(() => {
        if (!open) return;
        const scrollY = window.scrollY;
        const bodyStyle = document.body.style;
        const prev = {
            position: bodyStyle.position,
            top: bodyStyle.top,
            left: bodyStyle.left,
            right: bodyStyle.right,
            width: bodyStyle.width,
            overflow: bodyStyle.overflow,
        };
        bodyStyle.position = 'fixed';
        bodyStyle.top = `-${scrollY}px`;
        bodyStyle.left = '0';
        bodyStyle.right = '0';
        bodyStyle.width = '100%';
        bodyStyle.overflow = 'hidden';
        return () => {
            bodyStyle.position = prev.position;
            bodyStyle.top = prev.top;
            bodyStyle.left = prev.left;
            bodyStyle.right = prev.right;
            bodyStyle.width = prev.width;
            bodyStyle.overflow = prev.overflow;
            // Scroll pozisyonunu geri yükle
            window.scrollTo(0, scrollY);
        };
    }, [open]);

    // --- UNIT DEĞİŞİNCE: AppointmentDetailEdit.loadAppointment ile birebir mantık ---
    // Birim → firm.id → institutions yükle → default institution seç → appointmentType seç
    useEffect(() => {
        const run = async () => {
            if (!unitId) {
                setInstitutions([]);
                setOperationList([]);
                setInstitutionId(null);
                setAppointmentTypeId(null);
                return;
            }

            const unit = units.find(x => x.id === unitId);
            const firmId = unit?.firm?.id;

            // Paralel: institutions + operations
            const [_institutions, _operations] = await Promise.all([
                loadInstitutions(firmId),
                loadOperations(unitId)
            ]);

            // Default institution (AppointmentDetailEdit'teki birebir mantık)
            const defaultInst = _institutions?.length > 0
                ? (_institutions.find(x => x.isDefault) || _institutions[0])
                : null;

            // Default appointmentType
            const defaultAtId = defaultInst?.appointmentType?.id;

            // Fiyat listelerini önceden yükle
            if (defaultInst?.id) {
                await loadInstitutionPriceList(defaultInst.id, _operations.map(x => x.id));
            }
            if (defaultAtId) {
                await loadAppointmentTypePriceList(defaultAtId, _operations.map(x => x.id));
            }

            setInstitutionId(defaultInst?.id || null);
            setAppointmentTypeId(defaultAtId || null);
        };
        run();
    }, [unitId]);

    // Institution değişince fiyat listesi yenile
    useEffect(() => {
        if (institutionId && operationList.length > 0) {
            loadInstitutionPriceList(institutionId, operationList.map(x => x.id));
        }
    }, [institutionId]);

    // AppointmentType değişince fiyat listesi yenile
    useEffect(() => {
        if (appointmentTypeId && operationList.length > 0) {
            loadAppointmentTypePriceList(appointmentTypeId, operationList.map(x => x.id));
        }
    }, [appointmentTypeId]);

    // --- PRICE CALCULATION (AppointmentDetailEdit.onSelectOperation ile birebir mantık) ---
    const calculatePrice = (operation) => {
        const opId = operation.id;
        const institutionPrice = priceListRef.current.find(x => x.operationId === opId)?.price;
        const appTypePrice = appTypePriceListRef.current.find(x => x.operationId === opId)?.price;
        const appType = appointmentTypesRef.current.find(x => x.id === appointmentTypeId);

        let price = operation?.price;
        if (appType?.overrideInstitutionPrice && appTypePrice != null) {
            price = appTypePrice;
        } else if (institutionPrice != null) {
            price = institutionPrice;
        }
        return price;
    };

    // AppointmentDetailEdit.onSelectOperation ile birebir aynı yapı
    const onSelectOperation = (operationId) => {
        if (selectedOps.find(x => x.operationId === operationId)) return;
        const operation = operationList.find(x => x.id === operationId);
        if (!operation) return;

        const institutionPrice = priceListRef.current.find(x => x.operationId === operationId)?.price;

        setSelectedOps([
            ...selectedOps,
            {
                ...operation,
                id: v4(),
                operationId: operation.id,
                priceModified: false,
                price: calculatePrice(operation),
                institutionPrice: institutionPrice ?? operation?.price,
                discount: 0,
                subItems: [],
                operation,
                processMinute: operation.processMinute,
                accessionNumber: '',
            }
        ]);
    };

    const updateOperationData = (id, key, value) => {
        const ops = [...selectedOps];
        const index = ops.findIndex(x => x.id === id);
        if (index > -1) {
            const institutionPrice = priceListRef.current.find(x => x.operationId === ops[index]?.operationId)?.price;
            if (key === 'price') {
                if (institutionPrice > value) {
                    ops[index]['discount'] = 100 - (value * 100 / institutionPrice);
                } else {
                    ops[index]['discount'] = 0;
                }
            } else if (key === 'discount') {
                if (value > 0) {
                    ops[index]['price'] = institutionPrice - (institutionPrice * value / 100);
                } else {
                    ops[index]['price'] = institutionPrice;
                }
            }
            ops[index][key] = value;
        }
        setSelectedOps(ops);
    };

    const removeSelectedOp = (id) => {
        setSelectedOps(selectedOps.filter(x => x.id !== id));
    };

    const getInstitutionPrice = (operationId) =>
        priceListRef.current.find(x => x.operationId === operationId)?.price;

    const getAppointmentTypePrice = (operationId) =>
        appTypePriceListRef.current.find(x => x.operationId === operationId)?.price;

    // --- OTHER OPS ---
    const addOtherOp = () => {
        if (!otherForm.name || otherForm.price == null) {
            message.warning('Diğer işlem adı ve fiyatı gerekli');
            return;
        }
        setOtherOps([
            ...otherOps,
            { id: v4(), name: otherForm.name, price: otherForm.price }
        ]);
        setOtherForm({ name: '', price: 0 });
    };

    const removeOtherOp = (id) => {
        setOtherOps(otherOps.filter(x => x.id !== id));
    };

    // --- TOTALS ---
    const opsTotal = selectedOps.reduce((acc, x) => acc + (x.price || 0), 0);
    const otherTotal = otherOps.reduce((acc, x) => acc + (x.price || 0), 0);
    const grandTotal = opsTotal + otherTotal;

    // --- SAVE / CLOSE ---
    const handleSave = () => {
        if (selectedOps.length === 0 && otherOps.length === 0) {
            message.warning('En az bir işlem ekleyin');
            return;
        }

        const operationListPayload = selectedOps.map(x => ({
            id: x.id,
            operationId: x.operationId,
            operation: x.operation,
            price: x.price,
            priceModified: x.priceModified,
            institutionPrice: x.institutionPrice,
            discount: x.discount,
            subItems: x.subItems || [],
            processMinute: x.processMinute,
            accessionNumber: x.accessionNumber || '',
        }));

        const otherOpsPayload = otherOps.map(x => ({
            id: x.id,
            name: x.name,
            price: x.price,
        }));

        const savedUnit = units.find(u => u.id === unitId);
        const saved = PriceQuoteStorage.save({
            unitId,
            unitName: savedUnit?.name || '',
            firmName: savedUnit?.firm?.name || '',
            institutionId,
            appointmentTypeId,
            currency,
            operationList: operationListPayload,
            otherOperations: otherOpsPayload,
        });

        if (saved) {
            message.success('Fiyat teklifi hafızaya kaydedildi');
            onSaved && onSaved();
            handleClose();
        } else {
            message.error('Kaydedilemedi');
        }
    };

    const handleClose = () => {
        setUnitId(null);
        setInstitutionId(null);
        setAppointmentTypeId(null);
        setCurrency('TRY');
        setSelectedOps([]);
        setOtherOps([]);
        setOtherForm({ name: '', price: 0 });
        setEditingStep(null);
        setInstitutionSearch('');
        priceListRef.current = [];
        appTypePriceListRef.current = [];
        institutionIdRef.current = null;
        appointmentTypeIdRef.current = null;
        onClose && onClose();
    };

    const handleReset = () => {
        if (selectedOps.length === 0 && otherOps.length === 0) return;
        Modal.confirm({
            title: 'Listeyi sıfırla',
            content: 'Seçili tüm işlemler ve diğer kalemler silinecek. Emin misiniz?',
            okText: t('Yes'),
            cancelText: t('No'),
            onOk: () => {
                setSelectedOps([]);
                setOtherOps([]);
            }
        });
    };

    // Tüm adımlar tamamlanmadan işlem ekleme görünmesin
    const canAddOperations = !!unitId && !!institutionId && !!appointmentTypeId;
    const isPastDisable = false; // Fiyat teklifi tarih yok

    return (
        <>
            <style>{`
                .pq-compact-price .ant-input-number-input { padding: 0 4px !important; }
                .pq-compact-price .ant-input-number-prefix { margin-right: 2px !important; margin-left: 4px !important; font-size: 11px; opacity: 0.7; }
            `}</style>
        <Modal
            title={(
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 24 }}>
                    <span>Fiyat Teklifi Oluştur</span>
                    {(selectedOps.length + otherOps.length) > 0 && (
                        <Tag color="blue" style={{ margin: 0 }}>
                            {selectedOps.length + otherOps.length} kalem
                        </Tag>
                    )}
                </div>
            )}
            open={open}
            onCancel={handleClose}
            width={900}
            centered
            style={{
                maxWidth: 'calc(100vw - 16px)',
                top: isMobile ? 8 : undefined,
                paddingBottom: 0,
            }}
            bodyStyle={{
                // 100dvh: mobil tarayıcı bar'ları hesaba katılır (100vh iOS'ta yanlış)
                // Header (~55) + Footer (~140 toplam + butonlar) için yer bırak
                maxHeight: isMobile ? 'calc(100dvh - 220px)' : 'calc(100vh - 220px)',
                overflowY: 'auto',
                padding: isMobile ? 12 : 16,
                WebkitOverflowScrolling: 'touch',
            }}
            destroyOnClose
            footer={(
                <div>
                    {/* Toplam — action bar üstünde ince satır */}
                    {(selectedOps.length + otherOps.length) > 0 && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px 10px',
                            background: '#e6f7ff',
                            borderRadius: 4,
                            marginBottom: 8,
                            fontSize: 13
                        }}>
                            <span style={{ color: '#595959' }}>
                                Toplam {selectedOps.length + otherOps.length} kalem
                            </span>
                            <span style={{ fontWeight: 700, fontSize: 16, color: '#096dd9' }}>
                                {Helper.Currency.format(grandTotal, { currency })}
                            </span>
                        </div>
                    )}
                    {/* Aksiyon butonları */}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Button icon={<ClearOutlined />} onClick={handleReset} danger>
                            Sıfırla
                        </Button>
                        <Button onClick={handleClose}>
                            {t('Cancel')}
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            disabled={selectedOps.length === 0 && otherOps.length === 0}
                        >
                            Hafızaya Kaydet
                        </Button>
                    </div>
                </div>
            )}
        >
            {/* --- STEP-BY-STEP WIZARD: Birim → Kurum → Randevu Türü --- */}
            {(() => {
                const selectedUnit = units.find(u => u.id === unitId);
                const selectedInstitution = institutions.find(i => i.id === institutionId);
                const selectedType = appointmentTypesRef.current.find(at => at.id === appointmentTypeId);

                const StepHeader = ({ num, label, value, completed, isEditing, onToggle }) => {
                    const clickable = completed && onToggle;
                    return (
                        <div
                            onClick={clickable ? onToggle : undefined}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '8px 10px',
                                background: isEditing ? '#e6f7ff' : (completed ? '#f6ffed' : '#fafafa'),
                                border: `1px solid ${isEditing ? '#91d5ff' : (completed ? '#b7eb8f' : '#d9d9d9')}`,
                                borderRadius: 6,
                                marginBottom: 8,
                                cursor: clickable ? 'pointer' : 'default',
                                userSelect: 'none',
                                WebkitTapHighlightColor: 'transparent',
                            }}
                        >
                            <div style={{
                                width: 22, height: 22, borderRadius: '50%',
                                background: completed ? '#52c41a' : '#bfbfbf',
                                color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 12, fontWeight: 600, flexShrink: 0
                            }}>
                                {completed ? '✓' : num}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, color: '#8c8c8c', lineHeight: 1 }}>{label}</div>
                                {value && (
                                    <div style={{ fontSize: 13, fontWeight: 500, color: '#262626', marginTop: 2 }}>
                                        {value}
                                    </div>
                                )}
                            </div>
                            {clickable && (
                                <span style={{ fontSize: 11, color: isEditing ? '#1890ff' : '#1890ff' }}>
                                    {isEditing ? 'Kapat' : 'Değiştir'}
                                </span>
                            )}
                        </div>
                    );
                };

                const BigButtonGrid = ({ items, onPick, getKey, getLabel, getSubLabel }) => (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '140px' : '180px'}, 1fr))`,
                        gap: 8,
                        marginBottom: 12,
                    }}>
                        {items.map(item => (
                            <Button
                                key={getKey(item)}
                                size="large"
                                onClick={() => onPick(item)}
                                style={{
                                    height: 'auto',
                                    minHeight: 64,
                                    padding: '10px 12px',
                                    textAlign: 'left',
                                    whiteSpace: 'normal',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    justifyContent: 'center',
                                    gap: 2,
                                    lineHeight: 1.3,
                                }}
                            >
                                {getSubLabel && getSubLabel(item) && (
                                    <span style={{ fontSize: 10, color: '#8c8c8c', fontWeight: 400 }}>
                                        {getSubLabel(item)}
                                    </span>
                                )}
                                <span style={{ fontSize: 14, fontWeight: 500, color: '#262626' }}>
                                    {getLabel(item)}
                                </span>
                            </Button>
                        ))}
                    </div>
                );

                const isStep1Open = !unitId || editingStep === 1;
                const isStep2Open = unitId && (!institutionId || editingStep === 2);
                const isStep3Open = institutionId && (!appointmentTypeId || editingStep === 3);

                // Kurum step: arama + seçili kurum üstte
                const normalizedInstSearch = Helper.Text.normalizeText(institutionSearch || '');
                const filteredInstitutions = (() => {
                    let list = institutions;
                    if (normalizedInstSearch) {
                        list = list.filter(i => Helper.Text.normalizeText(i.name || '').includes(normalizedInstSearch));
                    }
                    if (institutionId) {
                        const selected = list.find(i => i.id === institutionId);
                        if (selected) {
                            list = [selected, ...list.filter(i => i.id !== institutionId)];
                        }
                    }
                    return list;
                })();

                return (
                    <div style={{ marginBottom: 12 }}>
                        {/* Step 1: Birim */}
                        <StepHeader
                            num={1}
                            label="Birim"
                            value={selectedUnit ? `${selectedUnit?.firm?.name ? selectedUnit.firm.name + ' — ' : ''}${selectedUnit.name}` : null}
                            completed={!!unitId}
                            isEditing={editingStep === 1}
                            onToggle={() => setEditingStep(editingStep === 1 ? null : 1)}
                        />
                        {isStep1Open && (
                            <BigButtonGrid
                                items={units}
                                getKey={(u) => u.id}
                                getLabel={(u) => u.name}
                                getSubLabel={(u) => u?.firm?.name || ''}
                                onPick={(u) => {
                                    if (editingStep === 1 && u.id === unitId) {
                                        setEditingStep(null);
                                        return;
                                    }
                                    setUnitId(u.id);
                                    if (editingStep === 1 && u.id !== unitId) {
                                        setInstitutionId(null);
                                        setAppointmentTypeId(null);
                                    }
                                    setEditingStep(null);
                                }}
                            />
                        )}

                        {/* Step 2: Kurum */}
                        {unitId && (
                            <>
                                <StepHeader
                                    num={2}
                                    label="Kurum"
                                    value={selectedInstitution?.name || null}
                                    completed={!!institutionId}
                                    isEditing={editingStep === 2}
                                    onToggle={() => {
                                        setEditingStep(editingStep === 2 ? null : 2);
                                        setInstitutionSearch('');
                                    }}
                                />
                                {isStep2Open && (
                                    institutions.length === 0 ? (
                                        <Alert type="info" message="Bu birime ait kurum yok" showIcon style={{ marginBottom: 12 }} />
                                    ) : (
                                        <>
                                            <Input
                                                allowClear
                                                size="middle"
                                                placeholder="Kurum ara..."
                                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                                value={institutionSearch}
                                                onChange={(e) => setInstitutionSearch(e.target.value)}
                                                style={{ marginBottom: 8 }}
                                            />
                                            {filteredInstitutions.length === 0 ? (
                                                <Empty
                                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                    description="Eşleşen kurum yok"
                                                    style={{ marginBottom: 12 }}
                                                />
                                            ) : (
                                                <BigButtonGrid
                                                    items={filteredInstitutions}
                                                    getKey={(i) => i.id}
                                                    getLabel={(i) => i.name}
                                                    getSubLabel={(i) => (
                                                        i.id === institutionId
                                                            ? '✓ Seçili'
                                                            : (i.isDefault ? '⭐ Varsayılan' : '')
                                                    )}
                                                    onPick={(i) => {
                                                        if (editingStep === 2 && i.id === institutionId) {
                                                            setEditingStep(null);
                                                            setInstitutionSearch('');
                                                            return;
                                                        }
                                                        const changed = editingStep === 2 && i.id !== institutionId;
                                                        setInstitutionId(i.id);
                                                        if (changed) setAppointmentTypeId(null);
                                                        setEditingStep(null);
                                                        setInstitutionSearch('');
                                                    }}
                                                />
                                            )}
                                        </>
                                    )
                                )}
                            </>
                        )}

                        {/* Step 3: Randevu Türü */}
                        {institutionId && (
                            <>
                                <StepHeader
                                    num={3}
                                    label="Randevu Türü"
                                    value={selectedType?.name || null}
                                    completed={!!appointmentTypeId}
                                    isEditing={editingStep === 3}
                                    onToggle={() => setEditingStep(editingStep === 3 ? null : 3)}
                                />
                                {isStep3Open && (
                                    appointmentTypesRef.current.length === 0 ? (
                                        <Alert type="info" message="Randevu türü yok" showIcon style={{ marginBottom: 12 }} />
                                    ) : (
                                        <BigButtonGrid
                                            items={
                                                appointmentTypeId
                                                    ? [
                                                        appointmentTypesRef.current.find(at => at.id === appointmentTypeId),
                                                        ...appointmentTypesRef.current.filter(at => at.id !== appointmentTypeId),
                                                    ].filter(Boolean)
                                                    : appointmentTypesRef.current
                                            }
                                            getKey={(at) => at.id}
                                            getLabel={(at) => at.name}
                                            getSubLabel={(at) => at.id === appointmentTypeId ? '✓ Seçili' : ''}
                                            onPick={(at) => {
                                                if (editingStep === 3 && at.id === appointmentTypeId) {
                                                    setEditingStep(null);
                                                    return;
                                                }
                                                setAppointmentTypeId(at.id);
                                                setEditingStep(null);
                                            }}
                                        />
                                    )
                                )}
                            </>
                        )}
                    </div>
                );
            })()}

            {/* AppointmentTypeSelect hidden — sadece appointmentTypesRef'i doldurmak için */}
            <div style={{ display: 'none' }}>
                <AppointmentTypeSelect
                    value={appointmentTypeId}
                    onChange={setAppointmentTypeId}
                    onTypes={(types) => { appointmentTypesRef.current = types; }}
                />
            </div>

            <Divider style={{ margin: '8px 0 16px 0' }} />

            {!canAddOperations && (
                <Alert
                    type="info"
                    showIcon
                    message="İşlemleri görmek için tüm adımları tamamlayın (Birim → Kurum → Tür)"
                    style={{ marginBottom: 12 }}
                />
            )}

            {/* --- OPERATION SELECT (AppointmentDetailEdit desktop ile aynı) --- */}
            {canAddOperations && (
                <>
                    {!isMobile && (
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>
                            İşlemler {selectedOps.length > 0 && `(${selectedOps.length})`}
                        </Text>
                    )}
                    {!isMobile ? (
                        <Select
                            style={{ width: '100%' }}
                            size="small"
                            autoClearSearchValue
                            placeholder={t('Select Operation')}
                            showSearch
                            filterOption={(input, option) =>
                                Helper.Text.normalizeText(option.children?.toString() || '').includes(Helper.Text.normalizeText(input))
                            }
                            mode="multiple"
                            autoClearSearchValue={false}
                            // Sadece mevcut birime ait (operationList'te bulunan) id'leri göster.
                            // selectedOps state'i bozulmaz — diğer birimlerin işlemleri tabloda durmaya devam eder.
                            value={selectedOps
                                .filter(x => operationList.some(o => o.id === x.operationId))
                                .map(x => x.operationId)}
                            onSelect={onSelectOperation}
                            open={operationSelectOpen}
                            onDropdownVisibleChange={setOperationSelectOpen}
                            onInputKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setTimeout(() => {
                                        setOperationSelectOpen(false);
                                        if (e.target && typeof e.target.blur === 'function') {
                                            e.target.blur();
                                        }
                                    }, 0);
                                }
                            }}
                            tagRender={(props) => (
                                <Tag
                                    closable
                                    style={{
                                        marginRight: 3,
                                        whiteSpace: 'normal',
                                        textAlign: 'left',
                                        wordBreak: 'break-word',
                                        height: 'auto',
                                        maxWidth: '100%',
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}
                                    onClose={() => {
                                        setSelectedOps(selectedOps.filter(x => x.operationId !== props.value));
                                    }}
                                >
                                    {props.label}
                                </Tag>
                            )}
                        >
                            {operationList.map(x => (
                                <Select.Option key={x.id} value={x.id}>
                                    {`${x.name} - ${x.processMinute} ${t('minute')}`}
                                </Select.Option>
                            ))}
                        </Select>
                    ) : (
                        <div>
                            {/* Mobil: başlık + buton tek satır */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                marginBottom: 6,
                            }}>
                                <Text strong style={{ fontSize: 14 }}>
                                    İşlemler {selectedOps.length > 0 && `(${selectedOps.length})`}
                                </Text>
                                <Button
                                    size="middle"
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={openOperationPicker}
                                >
                                    {t('Select Operation')}
                                </Button>
                            </div>

                            {/* iOS mobil klavye primer */}
                            <input
                                ref={operationKeyboardPrimerRef}
                                type="text"
                                inputMode="search"
                                aria-hidden="true"
                                tabIndex={-1}
                                readOnly
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    width: 1,
                                    height: 1,
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    border: 'none',
                                    background: 'transparent',
                                    zIndex: -1,
                                }}
                            />

                            <Modal
                                title={(
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 24 }}>
                                        <span>{t('Select Operation')}</span>
                                        <Tag color="blue" style={{ margin: 0 }}>
                                            {selectedOps.length} seçili
                                        </Tag>
                                    </div>
                                )}
                                open={operationPickerOpen}
                                onCancel={() => setOperationPickerOpen(false)}
                                footer={null}
                                width="100%"
                                style={{ top: 0, maxWidth: '100vw', margin: 0, padding: 0 }}
                                bodyStyle={{
                                    padding: 12,
                                    height: 'calc(100vh - 110px)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden'
                                }}
                                destroyOnClose
                            >
                                <Input
                                    ref={operationPickerSearchRef}
                                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                    placeholder={t('Search')}
                                    size="middle"
                                    value={operationPickerSearch}
                                    onChange={(e) => setOperationPickerSearch(e.target.value)}
                                    allowClear
                                    autoFocus
                                    style={{ marginBottom: 8 }}
                                />
                                <div style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 4,
                                    marginBottom: 8
                                }}>
                                    {operationList
                                        .filter(x => !operationPickerSearch || Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(operationPickerSearch)))
                                        .map(item => {
                                            const selected = selectedOps.some(x => x.operationId === item.id);
                                            return (
                                                <div
                                                    key={item.id}
                                                    onClick={() => {
                                                        if (selected) {
                                                            setSelectedOps(selectedOps.filter(x => x.operationId !== item.id));
                                                        } else {
                                                            onSelectOperation(item.id);
                                                        }
                                                    }}
                                                    style={{
                                                        padding: '12px 10px',
                                                        borderBottom: '1px solid #f5f5f5',
                                                        backgroundColor: selected ? '#e6f7ff' : 'white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 10,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <div style={{
                                                        width: 22, height: 22,
                                                        border: `2px solid ${selected ? '#1890ff' : '#d9d9d9'}`,
                                                        borderRadius: 4,
                                                        background: selected ? '#1890ff' : 'white',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        color: 'white', fontSize: 14, lineHeight: 1, flexShrink: 0
                                                    }}>
                                                        {selected && '✓'}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: selected ? 600 : 400, fontSize: 14, wordBreak: 'break-word' }}>
                                                            {item.name}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                                                            {item.processMinute} {t('minute')}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    {operationList.filter(x => !operationPickerSearch || Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(operationPickerSearch))).length === 0 && (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={t('No Data')}
                                            style={{ padding: 24 }}
                                        />
                                    )}
                                </div>
                                <Button
                                    type="primary"
                                    block
                                    size="large"
                                    onClick={() => setOperationPickerOpen(false)}
                                >
                                    Tamam ({selectedOps.length})
                                </Button>
                            </Modal>
                        </div>
                    )}

                    {/* --- SEÇİLİ İŞLEMLER --- mobilde kompakt satırlar, desktop'ta Table --- */}
                    {isMobile ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            marginTop: 8,
                            border: selectedOps.length > 0 ? '1px solid #f0f0f0' : 'none',
                            borderRadius: 6,
                            overflow: 'hidden'
                        }}>
                            {selectedOps.length === 0 && (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Operations')} />
                            )}
                            {selectedOps.map((op, idx) => (
                                <div
                                    key={op.id}
                                    style={{
                                        padding: '6px 8px',
                                        background: idx % 2 === 0 ? '#fff' : '#fafafa',
                                        borderBottom: idx < selectedOps.length - 1 ? '1px solid #f5f5f5' : 'none',
                                    }}
                                >
                                    {/* Ad + Fiyat + Sil tek satır */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{
                                            fontSize: 13,
                                            fontWeight: 500,
                                            color: '#262626',
                                            flex: 1,
                                            minWidth: 0,
                                            wordBreak: 'break-word',
                                            lineHeight: 1.3,
                                        }}>
                                            {op?.operation?.name}
                                        </span>
                                        <InputNumber
                                            size="small"
                                            className="pq-compact-price"
                                            prefix={Currency[currency]}
                                            value={op.price}
                                            step={0.01}
                                            min={0}
                                            inputMode="decimal"
                                            pattern="[0-9]*"
                                            onChange={(newPrice) => updateOperationData(op.id, 'price', newPrice)}
                                            style={{ width: 110, flexShrink: 0 }}
                                        />
                                        <Button
                                            size="small"
                                            danger
                                            type="text"
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeSelectedOp(op.id)}
                                            style={{ flexShrink: 0 }}
                                        />
                                    </div>

                                    {/* Info satırı: süre + ref fiyatlar */}
                                    <div style={{
                                        fontSize: 10,
                                        color: '#bfbfbf',
                                        marginTop: 2,
                                        display: 'flex',
                                        gap: 8,
                                        flexWrap: 'wrap'
                                    }}>
                                        {op.processMinute != null && (
                                            <span>⏱ {op.processMinute} dk</span>
                                        )}
                                        <span>Liste: {Helper.Currency.format(op?.operation?.price)}</span>
                                        {getInstitutionPrice(op?.operationId) != null && (
                                            <span>Kurum: {Helper.Currency.format(getInstitutionPrice(op?.operationId))}</span>
                                        )}
                                        {getAppointmentTypePrice(op?.operationId) != null && (
                                            <span>Tür: {Helper.Currency.format(getAppointmentTypePrice(op?.operationId))}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Table
                            style={{ marginTop: 12 }}
                            scroll={{ x: 600 }}
                            size="small"
                            dataSource={selectedOps}
                            pagination={false}
                            rowKey="id"
                            columns={[
                                {
                                    key: 'name',
                                    dataIndex: 'name',
                                    title: t('Name'),
                                    render: (_, row) => row?.operation?.name
                                },
                                {
                                    key: 'processMinute',
                                    dataIndex: 'processMinute',
                                    title: t('Process Minute'),
                                    width: 130,
                                    render: (text, row) => (
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            size="small"
                                            value={row?.processMinute}
                                            placeholder={t('Process Minute')}
                                            onChange={(val) => updateOperationData(row.id, 'processMinute', val)}
                                        />
                                    )
                                },
                                {
                                    key: 'price',
                                    dataIndex: 'price',
                                    width: 260,
                                    title: t('Price'),
                                    render: (_, row) => (
                                        <div>
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                size="small"
                                                prefix={Currency[currency]}
                                                value={row.price}
                                                step={0.01}
                                                min={0}
                                                onChange={(newPrice) => updateOperationData(row.id, 'price', newPrice)}
                                            />
                                            <table style={{ fontSize: 11, marginTop: 4 }}>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ paddingRight: 6, color: '#8c8c8c' }}><b>{t('Price')}:</b></td>
                                                        <td>{Helper.Currency.format(row?.operation?.price)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ paddingRight: 6, color: '#8c8c8c' }}><b>{t('Institution')} / {t('Special')}:</b></td>
                                                        <td>
                                                            {Helper.Currency.format(getInstitutionPrice(row?.operationId))}
                                                            {' / '}
                                                            {Helper.Currency.format(getAppointmentTypePrice(row?.operationId))}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                },
                                {
                                    key: '#',
                                    title: '#',
                                    width: 50,
                                    render: (_, row) => (
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeSelectedOp(row.id)}
                                        />
                                    )
                                }
                            ]}
                        />
                    )}
                </>
            )}

            {/* --- DIĞER KALEMLER --- */}
            <Divider style={{ margin: '16px 0 12px 0' }} />
            <Text strong>Diğer Kalemler ({otherOps.length})</Text>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, marginBottom: 8 }}>
                <Input
                    placeholder="Ad"
                    value={otherForm.name}
                    onChange={(e) => setOtherForm({ ...otherForm, name: e.target.value })}
                    size="middle"
                    style={{ flex: 1, minWidth: 0 }}
                    onPressEnter={addOtherOp}
                />
                <InputNumber
                    placeholder="Fiyat"
                    value={otherForm.price}
                    onChange={(val) => setOtherForm({ ...otherForm, price: val })}
                    size="middle"
                    min={0}
                    step={0.01}
                    prefix={Currency[currency]}
                    style={{ width: 130 }}
                />
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addOtherOp}
                    size="middle"
                />
            </div>
            {otherOps.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                    {otherOps.map(o => (
                        <div
                            key={o.id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '6px 10px',
                                background: '#fafafa',
                                borderRadius: 4
                            }}
                        >
                            <span style={{ flex: 1, minWidth: 0 }}>{o.name}</span>
                            <span style={{ fontWeight: 600, marginRight: 8 }}>{Helper.Currency.format(o.price, { currency })}</span>
                            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeOtherOp(o.id)} />
                        </div>
                    ))}
                </div>
            )}

        </Modal>
        </>
    );
}
